import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import Input from "@/app/components/ui/Inputs/Input";
import { PRIMARY_BUTTON, SECONDARY_BUTTON } from "@/constants";
import { useNavigate, useParams } from "react-router";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import { ISubCategoryOnboardPostBody } from "@/api_framework/api_modals/Inventory";
import SearchableSelectMenu from "@/app/components/ui/SelectMenus/SearchableSelectMenu";
import { useCouponCategoryAPI } from "@/app/hooks/api_hooks/coupon/useCouponCategoryAPI";
import { useEMRSectionAPI } from "@/app/hooks/api_hooks/Inventory/SubCategory/useEMRSectionAPI";
import { useSubCategoryUpdateAPI } from "@/app/hooks/api_hooks/Inventory/SubCategory/useSubCategoryUpdateAPI";
import { useSubCategoryDetailAPI } from "@/app/hooks/api_hooks/Inventory/SubCategory/useSubCategoryDetailAPI";
import { useSubCategoryOnboardAPI } from "@/app/hooks/api_hooks/Inventory/SubCategory/useSubCategoryOnboardAPI";

export interface ISubCategoryOnboardProps {
  sub_category_name: string;
  emr: string;
  type: string;
  category: string;
  sub_category_id: string;
}

const types = [
  {
    id: "1",
    title: "TAMS",
  },
  {
    id: "2",
    title: "Clinic",
  },
];

const SubCategorySetupOnboard = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { getRouteKey } = useRouter();
  const { emrSectionList } = useRouteInfo("INVENTORY")?.routeState?.state as IInventoryState;
  const emrOptions = useSelectMenuReducer(emrSectionList, "emr_category_name", "id");
  const fetchEmrSectionList = useEMRSectionAPI();
  const { execute: addSubCategory } = useSubCategoryOnboardAPI();
  const { execute: fetchSubCategoryDetail, subCategoryDetail } = useSubCategoryDetailAPI();
  const { execute: updateSubCategory } = useSubCategoryUpdateAPI();
  const { execute: fetchCategory, serviceCategory } = useCouponCategoryAPI();
  const categoryOptions = useSelectMenuReducer(serviceCategory, "category_name", "id");

  const formHook = useForm<ISubCategoryOnboardProps>({
    mode: "onChange",
    defaultValues: {},
  });

  useEffect(() => {
    fetchEmrSectionList();
    if (params["sub_category_id"]) {
      fetchSubCategoryDetail(params["sub_category_id"]);
    }
  }, []);

  useEffect(() => {
    if (subCategoryDetail) {
      formHook.setValue("sub_category_name", subCategoryDetail?.subcategory_name);
      formHook.setValue("emr", subCategoryDetail?.emr_search);
      formHook.setValue("type", subCategoryDetail?.subcategory_tagging);
      formHook.setValue("category", subCategoryDetail?.category_id);
    }
  }, [subCategoryDetail]);

  const onSubmit = (data: ISubCategoryOnboardProps) => {
    if (data) {
      const constructedBody: ISubCategoryOnboardPostBody = {
        subcategory_name: data?.sub_category_name,
        emr_search: data?.emr,
        subcategory_tagging: data?.type,
        category_id: data?.category,
      };
      if (params["sub_category_id"]) {
        updateSubCategory({
          ...constructedBody,
          subcategory_id: params["sub_category_id"],
        }).then(() => {
          navigate(`/app/inventory/sub-category-setup`);
        });
      } else {
        addSubCategory(constructedBody).then(({ status }) => {
          if (status) {
            navigate(`/app/inventory/sub-category-setup`);
          }
        });
      }
    }
  };

  useEffect(() => {
    fetchCategory();
  }, []);

  return (
    <>
      <div className="bg-white  px-4 sm:px-6 lg:px-8 py-3 rounded-md">
        <div className="sm:flex-auto">
          <h1 className="font-semibold text-base text-gray-900 leading-6">Add New Sub-Category</h1>
        </div>
      </div>
      <div className="bg-white mt-2 px-4 sm:px-6 lg:px-8 py-5 rounded-md">
        <form className="w-full pb-24" onSubmit={formHook.handleSubmit(onSubmit)}>
          <div className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-4">
            <div className="w-full shrink-0 text-sm sm:w-48">
              <p>
                Sub-Category Name <span className="text-red-400">*</span>
              </p>
            </div>
              <div className="w-full sm:w-[320px]">
              <Input
                register={formHook?.register("sub_category_name", {
                  required: true,
                })}
                type="text"
                fieldError={formHook?.formState?.errors?.sub_category_name}
                errorMessages={[{ type: "required", message: "Sub-Category Name is required" }]}
                className="border-slate-400 rounded-md w-full h-9 text-sm"
                inputClassName="h-9"
                placeholder="Enter Sub-Category Name"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  formHook?.setValue("sub_category_name", e.target.value);
                  formHook?.setValue("sub_category_id", e.target.id);
                  formHook.clearErrors("sub_category_name");
                }}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5 mt-6 sm:flex-row sm:items-center sm:gap-4">
            <div className="w-full shrink-0 text-sm sm:w-48">
              <p>
                EMR Section <span className="text-red-400">*</span>
              </p>
            </div>
              <div className="w-full sm:w-[320px]">
              <SearchableSelectMenu
                errorMessages={[{ type: "required", message: "EMR Section is required" }]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("emr", item.title);
                  }
                }}
                fieldError={formHook.formState.errors.emr}
                register={formHook.register("emr", { required: true })}
                selectItems={emrOptions}
                placeholder="Select EMR Section"
                showTooltips={true}
                showTypedErrors={false}
                showDropdownIcon={true}
                defaultSelected={emrOptions?.filter((oc) => oc.title === formHook.watch("emr"))[0]}
                listBoxClassName="w-full"
                className="text-gray-800"
              />
            </div>
          </div>
          <div className="flex flex-col gap-1.5 mt-6 sm:flex-row sm:items-center sm:gap-4">
            <div className="w-full shrink-0 text-sm sm:w-48">
              <p>
                Type <span className="text-red-400">*</span>
              </p>
            </div>
              <div className="w-full sm:w-[320px]">
              <SearchableSelectMenu
                errorMessages={[{ type: "required", message: "Type is required" }]}
                onSelectItem={(item) => {
                  if (item) {
                    formHook.setValue("type", item.title);
                  }
                }}
                fieldError={formHook.formState.errors.type}
                register={formHook.register("type", { required: true })}
                selectItems={types}
                placeholder="Select Type"
                showTooltips={true}
                showTypedErrors={false}
                showDropdownIcon={true}
                defaultSelected={types?.filter((oc) => oc.title === formHook.watch("type"))[0]}
                listBoxClassName="w-full"
                className="text-gray-800"
              />
            </div>
          </div>
          <div className="fixed inset-x-0 bottom-0 z-20 flex justify-end items-center gap-4 border-t-2 w-full p-4 bg-white">
            <button
              type="button"
              onClick={() => navigate(getRouteKey("SUB_CATEGORY_SETUP", "url"))}
              className={`${SECONDARY_BUTTON} flex-1 sm:flex-none`}
            >
              Back
            </button>
            <button type="submit" className={`${PRIMARY_BUTTON} flex-1 sm:flex-none`}>
              Save
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default SubCategorySetupOnboard;
